import React, { useRef, useEffect } from 'react'
import { Form, Formik } from 'formik'
import { Column, Row } from '@/components/layout/layout'
import FormItem from '@/views/main/modules/goods-manage/components/form/form-item/form-item'
import { getErrorStr, checkError, checkValidate } from '@/utils/form'
import Input from '@/components/input/input'
import { getBEMCls } from '@/utils/classname'
import { layerManage } from '@/utils/layer'
import Button from '@/components/button/button'
import FormTitle from '@/views/main/modules/goods-manage/components/form/form-title/form-title'
import SalePrice from '@/views/main/modules/goods-manage/goods-storage/goods-library/modules/edit-goods/modules/form/sale-price/sale-price'
import { IPricingMethod } from '@/views/main/modules/goods-manage/goods-storage/goods-library/modules/edit-goods/edit-goods'
import { goodsModel } from '@/views/main/modules/goods-manage/goods-storage/goods-library/modules/edit-goods/modules/model'
import { SlideShow } from '../slide-show/slide-show'
import { Unit } from '@/model/types/goods'
import { $post } from '@/api'
import $msg from '@/utils/message'
import { useRequest } from 'ahooks'
import { goodsShelfStore } from '../../../../store/goods-shelf'
import MallMediaEdit, { MediaItemType } from '../media-edit/media-edit'
import { isExist } from '@/utils'
import { buildMallDisplayPayload } from './mall-display'

export interface IDetailForm {
    title: string
    goods_brief: string
    sales_unit?: Unit
    stock: number | string
    content: {
        descriptions?: MediaItemType[]
    }
    negative_stock_mode: 0 | 2 //  {"0": "允许", "2": "禁止"}
    pricing_methods: IPricingMethod[]
    mall_goods_images: {
        id: number
        img_url: string
    }[]
}

export interface IDetailGoods extends IDetailForm {
    goods: {
        stock: number
        [key: string]: any
    }
}

interface IDetailFormErrors {
    title?: string
    stock?: string
    pricing_methods: {
        member_price?: string
        sales_unit_ratio?: string
        barcodes: string[]
        sale_prices: {
            threshold?: string
            sale_price?: string
        }[]
    }[]
}

interface DetailFormProps {
    detail: IDetailGoods | null
    goodId: number
}

const DetailForm: React.FC<DetailFormProps> = props => {
    const formikRef = useRef(null)
    const cls = (...args: string[]) => getBEMCls('goods-detail', ...args)
    const mallGoodDes = useRef<MediaItemType[]>([])

    useEffect(() => {
        // 初始化商品需要的信息
        goodsModel.init()
    }, [])

    useEffect(() => {
        if (props.detail) {
            // @ts-ignore
            formikRef.current && formikRef.current.setValues(props.detail)
        }
    }, [props.detail])

    const formInitValues: IDetailForm = {
        title: '',
        goods_brief: '',
        stock: '',
        negative_stock_mode: 0,
        mall_goods_images: [],
        content: {},
        // 多售价
        pricing_methods: [
            {
                isShowMemberPriceLimit: false,
                pricing_method_id: null, // 计价方式id
                sales_unit_id: 2, // 计价单位ID
                sales_unit_ratio: '', //  计价单位换算率
                method_type: 0,
                sale_prices: [
                    {
                        sale_price: '',
                        threshold: 0,
                        sale_unit: {
                            id: 2,
                            name: '斤',
                            status: 0,
                        },
                    },
                ],
                member_price: {
                    sale_price: '',
                    amount_limit: '',
                    sale_unit: {
                        id: 2,
                        name: '斤',
                        status: 0,
                    },
                },
                mall_display: {
                    underline_price: '',
                },
                price_annotation: '', // 计价说明
                price_code: '', // 商品编码
                scale_num: '', // 条码秤序号
                barcodes: [''],
                sales_unit: {
                    id: 2,
                    name: '斤',
                    status: 0,
                },
            },
        ],
    }

    const submitReq = useRequest(
        (val: IDetailForm) => {
            const priceMethods = val.pricing_methods.map(method => ({
                ...method,
                ...buildMallDisplayPayload(method.mall_display?.underline_price),
                member_price: method.member_price.sale_price
                    ? {
                          amount_limit: method.member_price.amount_limit || null,
                          sale_price: method.member_price.sale_price,
                          sale_unit: method.member_price.sale_unit.id,
                      }
                    : null,
                sale_prices: method.sale_prices.map(sale => ({
                    ...sale,
                    sale_unit: sale.sale_unit.id,
                })),
                sales_unit_id: method.sale_prices[0].sale_unit.id,
            }))

            return $post({
                url: '/mall/editmallgoods',
                params: {
                    ...val,
                    content: isExist(mallGoodDes)
                        ? {
                              descriptions: mallGoodDes.current,
                          }
                        : void 0,
                    mall_goods_id: props.goodId,
                    img_urls: val.mall_goods_images.map(img => img.img_url || 'FkvY8zkeCTU97-uwebfxO4m9IQgi'),
                    pricing_methods: priceMethods,
                    mall_goods_images: void 0,
                },
            })
        },
        {
            manual: true,
            onSuccess: () => {
                $msg('success', '更新成功')
                layerManage.pop()
                goodsShelfStore.setUpdateGroupStatus(!goodsShelfStore.updateGroupStatus)
            },
            onError: e => $msg('error', e.message),
        },
    )

    const onSubmit = (val: IDetailForm) => {
        submitReq.run(val)
    }

    const validateForm = (values: IDetailForm) => {
        const errors: IDetailFormErrors = {
            pricing_methods: [],
        }

        values.pricing_methods.forEach((pricingMethod, priceingMethodIndex) => {
            errors.pricing_methods.push({
                sale_prices: [],
                barcodes: [],
            })

            if (pricingMethod.sales_unit_ratio.toString().trim().length === 0) {
                errors.pricing_methods![priceingMethodIndex].sales_unit_ratio = '请输入换算规则'
            }

            pricingMethod.sale_prices.forEach((value, salePriceIndex, arr) => {
                errors.pricing_methods[priceingMethodIndex].sale_prices.push({})
                if (arr[salePriceIndex - 1]) {
                    if (+value.threshold < +arr[salePriceIndex - 1].threshold) {
                        errors.pricing_methods[priceingMethodIndex].sale_prices[salePriceIndex].threshold =
                            '应大于上一级'
                    }

                    if (+value.sale_price > +arr[salePriceIndex - 1].sale_price) {
                        errors.pricing_methods[priceingMethodIndex].sale_prices[salePriceIndex].sale_price =
                            '应小于上一级'
                    }
                }

                if (value.sale_price.toString().trim().length === 0) {
                    errors.pricing_methods[priceingMethodIndex].sale_prices[salePriceIndex].sale_price = '请输入价格'
                }

                if (value.threshold.toString().trim().length === 0) {
                    errors.pricing_methods[priceingMethodIndex].sale_prices[salePriceIndex].threshold = '请输入购买量'
                }
            })

            if (+pricingMethod.member_price.sale_price > +pricingMethod.sale_prices[0].sale_price) {
                errors.pricing_methods[priceingMethodIndex].member_price = '会员价需小于零售价'
            }

            pricingMethod.barcodes.forEach(val => {
                const str = val.length > 0 && val.length < 8 ? '条码需大于8位' : ''
                errors.pricing_methods[priceingMethodIndex].barcodes.push(str)
            })
        })

        if (values.title.trim().length === 0) {
            errors.title = '请输入商品标题'
        }

        if (props.detail && +values.stock > props.detail.goods.stock) {
            errors.stock = '商城库存需小于货品库库存，请先更改商品库库存'
        }

        if (checkValidate(errors)) {
            return {}
        }

        return errors
    }

    return (
        <Formik
            validateOnBlur
            innerRef={formikRef}
            initialValues={formInitValues}
            onSubmit={onSubmit}
            validate={validateForm}
        >
            {({ values, handleChange, handleBlur, errors, touched }) => {
                return (
                    <Form>
                        <Column data-margin-bottom-80>
                            <FormTitle title="详情页设置" />
                            <SlideShow />
                            <FormItem isRequire title="商品标题" error={getErrorStr('title', errors, touched)}>
                                <Input
                                    error={checkError('title', errors, touched)}
                                    name="title"
                                    type="text"
                                    maxLength={30}
                                    value={values.title}
                                    onChange={handleChange}
                                    onBlur={handleBlur}
                                    placeholder="输入商品标题"
                                    inputsuffix={() => (
                                        <span data-font-size-14 data-color-000>
                                            {values.title.length}/30
                                        </span>
                                    )}
                                />
                            </FormItem>
                            <FormItem title="商品简介">
                                <Input
                                    name="goods_brief"
                                    type="text"
                                    maxLength={30}
                                    value={values.goods_brief}
                                    onChange={handleChange}
                                    onBlur={handleBlur}
                                    placeholder="填写后将会展示在微商城商品详情页"
                                    inputsuffix={() => (
                                        <span data-font-size-14 data-color-000>
                                            {values.goods_brief.length}/30
                                        </span>
                                    )}
                                />
                            </FormItem>
                            <FormItem title="图文详情">
                                <MallMediaEdit
                                    val={values.content?.descriptions}
                                    onChange={val => {
                                        mallGoodDes.current = val
                                    }}
                                />
                            </FormItem>
                            <FormTitle title="基础设置" />
                            <FormItem title="商城库存" error={getErrorStr('stock', errors, touched)}>
                                <Input
                                    error={checkError('stock', errors, touched)}
                                    name="stock"
                                    value={values.stock}
                                    onChange={handleChange}
                                    onBlur={handleBlur}
                                    type="number"
                                    placeholder="请输入商品库存"
                                />
                            </FormItem>
                            <SalePrice showPriceType unitName={values.sales_unit?.name} />
                        </Column>
                        <Row data-justify-content-space-between className={cls('footer')}>
                            <Button loading={submitReq.loading || void 0} type="submit" types="default">
                                确认
                            </Button>
                        </Row>
                    </Form>
                )
            }}
        </Formik>
    )
}

export default DetailForm
